import { QCONTENT_DETAILS_DATA_TEST_ID } from '@e2e/constants/exam-management';
import { useItemSet } from '@exam-management/common/item-set-provider';
import { ITEM_SET_STATUS_TEXT, QCONTENT_COLOR_STATUS } from '@exam-management/components';
import { QuestionGroupStatus } from '@exam-management/graphql';
import { ItemSetExportFormModal } from '@exam-management/item-set-export';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import {
  CircleIcon,
  ColorPalette,
  Divider,
  FileDownloadOutlinedIcon,
  Flex,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  MoreVertIcon,
  styled,
  Text,
  Tooltip,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { usePermission, WithPermission } from '@shared/permissions';
import { Fragment, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { DisableItemSetQuestionModal } from '../disable-item-set-question-modal';
import { PendingItemSetQuestionModal } from '../pending-item-set-question-modal';
import { PublishItemSetModal } from '../publish-item-set-modal';

const StyledMenuSection = styled(Flex)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(1),
  width: '100%',
  padding: theme.spacing(2),
}));

export default function ItemSetQuestionDetailHeaderMenu() {
  const {
    itemSet: { id: itemSetId, status, questions },
    isPublishing,
    isUpdatingToPending,
    isDisabling,
  } = useItemSet();

  const { formState } = useFormContext();
  const { permissions } = usePermission();

  const isMutating = formState.isSubmitting || isPublishing || isUpdatingToPending || isDisabling;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = anchorEl !== null;

  const [isPublishModalOpen, publishModalHandlers] = useDisclosure(false);
  const { open: openPublishModal, close: closePublishModal } = publishModalHandlers;

  const [isPendingModalOpen, pendingModalHandlers] = useDisclosure(false);
  const { open: openPendingModal, close: closePendingModal } = pendingModalHandlers;

  const [isDisableModalOpen, disableModalHandlers] = useDisclosure(false);
  const { open: openDisableModal, close: closeDisableModal } = disableModalHandlers;

  const [isItemSetExportModalOpen, itemSetExportModalHandlers] = useDisclosure(false);
  const { open: openItemSetExportModal, close: closeItemSetExportModal } =
    itemSetExportModalHandlers;

  const handleItemSetExportClick = () => {
    openItemSetExportModal();
    handleClose();
  };

  const handlePublishItemSetClick = () => {
    openPublishModal();
    handleClose();
  };

  const handlePendingItemSetClick = () => {
    openPendingModal();
    handleClose();
  };

  const handleDisableItemSetClick = () => {
    openDisableModal();
    handleClose();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const statusMenuOptions = useMemo(
    () => [
      {
        label: ITEM_SET_STATUS_TEXT[QuestionGroupStatus.Published],
        onClick: handlePublishItemSetClick,
        color: QCONTENT_COLOR_STATUS[QuestionGroupStatus.Published],
        status: [QuestionGroupStatus.Draft, QuestionGroupStatus.Unpublished],
        requirePermissions: [APP_PERMISSIONS.QBANK.QUESTION.STATUS],
        disabled: isMutating,
        disabledTooltip: 'Saving in progress',
      },
      {
        label: ITEM_SET_STATUS_TEXT[QuestionGroupStatus.Unpublished],
        onClick: handlePendingItemSetClick,
        color: QCONTENT_COLOR_STATUS[QuestionGroupStatus.Unpublished],
        status: [QuestionGroupStatus.Published],
        requirePermissions: [APP_PERMISSIONS.QBANK.QUESTION.STATUS],
        disabled: isMutating,
        disabledTooltip: 'Saving in progress',
      },
      {
        label: ITEM_SET_STATUS_TEXT[QuestionGroupStatus.Disabled],
        onClick: handleDisableItemSetClick,
        color: QCONTENT_COLOR_STATUS[QuestionGroupStatus.Disabled],
        status: [QuestionGroupStatus.Unpublished],
        requirePermissions: [APP_PERMISSIONS.QBANK.QUESTION.STATUS],
        disabled: isMutating,
        disabledTooltip: 'Saving in progress',
      },
    ],
    [isMutating],
  );

  const filteredStatusMenuOptions = useMemo(
    () =>
      statusMenuOptions.filter(
        (option) =>
          option.status.includes(status) &&
          option.requirePermissions.every((reqPermission) => permissions.includes(reqPermission)),
      ),
    [status, statusMenuOptions, permissions],
  );

  const hasSubQuestion = Array.isArray(questions) && !!questions.length;

  const actionMenuOptions = [
    {
      name: 'EXPORT',
      component: (
        <MenuItem value='item-set-export' onClick={handleItemSetExportClick}>
          <FileDownloadOutlinedIcon
            sx={(theme) => ({
              fontSize: '24px',
              mr: theme.spacing(2),
            })}
          />
          <ListItemText>Export Item Set</ListItemText>
        </MenuItem>
      ),
      enabled: hasSubQuestion && status !== QuestionGroupStatus.PreDraft,
      requiredPermissions: [APP_PERMISSIONS.QBANK.QUESTION.VIEW],
    },
  ];

  const filteredActionMenuOptions = useMemo(
    () =>
      actionMenuOptions.filter(
        (option) =>
          option.enabled &&
          option.requiredPermissions.every((reqPermission) => permissions.includes(reqPermission)),
      ),
    [actionMenuOptions],
  );

  const shouldDisplayStatusMenu = !!filteredStatusMenuOptions.length;
  const shouldDisplayActionMenu = !!filteredActionMenuOptions.length;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (shouldDisplayStatusMenu || shouldDisplayActionMenu) {
      setAnchorEl(event.currentTarget);
    }
  };

  if (!shouldDisplayActionMenu && !shouldDisplayStatusMenu) {
    return null;
  }

  return (
    <>
      <Divider orientation='vertical' flexItem sx={{ width: 'unset' }} />
      <IconButton
        data-testid={QCONTENT_DETAILS_DATA_TEST_ID.ITEM_SET_HEADER_MENU}
        sx={(theme) => ({
          borderRadius: theme.shape.rounded(1),
          border: `1px solid ${theme.palette.grey[400]}`,
        })}
        onClick={handleClick}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        MenuListProps={{
          sx: (theme) => ({
            borderRadius: theme.shape.rounded(2),
            width: '180px',
            p: theme.spacing(0),
          }),
        }}
      >
        {shouldDisplayStatusMenu ? (
          <WithPermission requiredPermission={APP_PERMISSIONS.QBANK.QUESTION.STATUS}>
            <StyledMenuSection>
              <Text
                variant='captionSemibold'
                sx={(theme) => ({
                  px: theme.spacing(2),
                })}
              >
                Update Status to
              </Text>
              {filteredStatusMenuOptions.map(
                ({ label, onClick, color, disabled, disabledTooltip }) => (
                  <Tooltip key={label} title={disabled ? disabledTooltip : ''} placement='left'>
                    <span>
                      <MenuItem onClick={onClick} disabled={disabled} sx={{ width: '100%' }}>
                        <CircleIcon
                          sx={(theme) => ({
                            color: Object.values(ColorPalette).includes(color as ColorPalette)
                              ? theme.palette[color as ColorPalette].main
                              : color,
                            fontSize: '10px',
                            mr: theme.spacing(2),
                          })}
                        />
                        <ListItemText>{label}</ListItemText>
                      </MenuItem>
                    </span>
                  </Tooltip>
                ),
              )}
            </StyledMenuSection>
          </WithPermission>
        ) : null}
        {shouldDisplayStatusMenu && shouldDisplayActionMenu ? <Divider /> : null}
        {shouldDisplayActionMenu ? (
          <StyledMenuSection>
            <Text
              variant='captionSemibold'
              sx={(theme) => ({
                px: theme.spacing(2),
              })}
            >
              Actions
            </Text>
            {filteredActionMenuOptions.map(({ name, component }) => (
              <Fragment key={name}>{component}</Fragment>
            ))}
          </StyledMenuSection>
        ) : null}
      </Menu>
      <PublishItemSetModal open={isPublishModalOpen} onClose={closePublishModal} />
      <PendingItemSetQuestionModal open={isPendingModalOpen} onClose={closePendingModal} />
      <DisableItemSetQuestionModal open={isDisableModalOpen} onClose={closeDisableModal} />
      <ItemSetExportFormModal
        open={isItemSetExportModalOpen}
        onClose={closeItemSetExportModal}
        questionGroupId={itemSetId}
      />
    </>
  );
}
